import React from 'react';
import styled from 'styled-components/macro';
import { Body, Colors } from '@cognite/cogs.js';
import {
  isConditionWithValueLabel,
  LogicalCondition,
  LogicalRule,
  RelationalRule,
} from 'utils/rules';
import { getConditionLabel } from 'utils/project-ruleset';

export const ConditionsTreeSummary = ({
  prefix,
  conditionsTree,
  maxTreeHeight,
}: {
  prefix: string;
  conditionsTree: LogicalRule | null;
  maxTreeHeight?: number;
}) => {
  if (!conditionsTree) return null;

  const renderRelationalCondition = (
    item: RelationalRule,
    logicalOperator: LogicalCondition,
    index: number,
    countConditions: number
  ) => {
    const { condition, property, value } = item;
    const showLogicOperatorAfterCondition =
      logicalOperator && index !== countConditions - 1;
    const showConditionValueLabel = isConditionWithValueLabel(condition);

    return (
      <>
        <StyledRuleInline key={`${prefix}-rule`} level={2}>
          <StyledSpanBold>{property}</StyledSpanBold>
          <StyledRuleInlineCondition>
            {getConditionLabel(condition)}
          </StyledRuleInlineCondition>
          {showConditionValueLabel && <StyledSpanBold>{value}</StyledSpanBold>}
        </StyledRuleInline>
        {showLogicOperatorAfterCondition && (
          <StyledLogicalOperator>{logicalOperator}</StyledLogicalOperator>
        )}
      </>
    );
  };

  const renderTree = (tree: LogicalRule, treeNestLevel: number) =>
    tree.children.map((nestOrCondition, index) => {
      const itemId = index;

      if (nestOrCondition._type === 'LogicalRule') {
        const showLogicOperatorAfterTree =
          tree.condition && index !== tree.children.length - 1;

        return (
          <StyledGroup key={itemId} nestLevel={treeNestLevel + 1}>
            {renderTree(nestOrCondition, treeNestLevel + 1)}
            {showLogicOperatorAfterTree && (
              <StyledLogicalOperator>{tree.condition}</StyledLogicalOperator>
            )}
          </StyledGroup>
        );
      }
      if (nestOrCondition._type === 'RelationalRule') {
        return renderRelationalCondition(
          nestOrCondition,
          tree.condition || LogicalCondition.AND,
          index,
          tree.children.length
        );
      }
      return null;
    });

  return (
    <StyledTree maxTreeHeight={maxTreeHeight || 200}>
      {renderTree(conditionsTree, 1)}
    </StyledTree>
  );
};

const StyledTree = styled.div<{ maxTreeHeight: number }>`
  display: flex;
  flex-direction: column;
  max-height: ${(props) => props.maxTreeHeight}px;
  overflow: auto;
`;

const StyledSpanBold = styled.span`
  font-weight: 500;
`;

const StyledRuleInline = styled(Body)`
  display: inline-flex;
  gap: 0.3rem;
  ${StyledSpanBold} {
    font-weight: 700;
  }
`;

const StyledRuleInlineCondition = styled.span`
  text-transform: lowercase;
`;

const StyledGroup = styled.div<{ nestLevel: number }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  border-radius: 10px;
  margin: 8px 0;
  ${(props) => {
    if (props.nestLevel < 2) return ``;
    if (props.nestLevel % 2 === 0) {
      return `
      background-color: var(--cogs-midblue-8);
      padding: 8px 2rem;
      border: 1px solid var(--cogs-midblue-5);
    `;
    }
    return `
      background-color:  var(--cogs-midblue-7);
      padding: 8px 2rem;
      border: 1px solid var(--cogs-midblue-5);
    `;
  }};
`;

const StyledLogicalOperator = styled.span`
  font-weight: 500;
  font-style: italic;
`;
